import { useFlag } from '@afterpaytouch/feature'
import { parseCookies } from 'nookies'
import { OnfileApplePay } from '@afterpaytouch/portal-api/consumer/checkout/types'
import { ApplePayEntryData, ApplePaylaterInitialState, CheckoutAppSelector, useAppSelector, useCheckoutState } from '..'
import { APPLE_ORDER_SOURCES, Features } from '../../model'
import { FLOW_COOKIE_NAME, FlowCookieValue } from '../../utils/server/flowCookie'
import { isNullOrUndefined } from '@afterpay/utils'

export const applePaylaterSelector: CheckoutAppSelector<ApplePaylaterInitialState> = (state) => state.applePaylater

export const useApplePayEntryData = (): ApplePayEntryData | undefined => {
  const applePayEntryData = useAppSelector(applePaylaterSelector)
  return applePayEntryData.entryData
}

export const useIsApplePaylaterEnabled = (): boolean => {
  return useFlag(Features.APPLE_PAYLATER_INTEGRATION)
}

export const useIsApplePaylater = (): boolean => {
  const { checkout } = useCheckoutState()

  const cookies = parseCookies()
  const flowCookie = cookies[FLOW_COOKIE_NAME] as FlowCookieValue | undefined
  const fromCookie = flowCookie === FlowCookieValue.ApplePaylater

  const isApplePaylaterMerchant = checkout?.merchant?.source ? APPLE_ORDER_SOURCES.includes(checkout.merchant.source) : false

  return fromCookie && isApplePaylaterMerchant
}

const APPLE_PAYLATER_LINK_QUERY = 'hide_navigation=true&hide_footer=true'
const AFTERPAY_HOST_PATTERN = /(^|\.)afterpay\.com$/i

const isAfterpayUrl = (url: string): boolean => {
  try {
    return AFTERPAY_HOST_PATTERN.test(new URL(url).hostname)
  } catch {
    return false
  }
}

const appendApplePaylaterLinkParams = (url: string): string => {
  if (!url || !isAfterpayUrl(url)) {
    return url
  }
  const [base, hash = ''] = url.split('#')
  const separator = base.includes('?') ? '&' : '?'
  const hashSuffix = hash ? `#${hash}` : ''
  return `${base}${separator}${APPLE_PAYLATER_LINK_QUERY}${hashSuffix}`
}

export const useApplePaylaterLinkParams = (): ((url: string) => string) => {
  const isApplePaylater = useIsApplePaylater()
  return (url: string) => (isApplePaylater ? appendApplePaylaterLinkParams(url) : url)
}

export const useIsApplePaylaterWithApplePayRepayment = (): boolean => {
  const isApplePaylater = useIsApplePaylater()
  const isAppleRepaymentEnabled = useFlag<boolean>(Features.ENABLE_APPLE_PAYLATER_APPLE_PAY_REPAYMENT, false)
  return isAppleRepaymentEnabled && isApplePaylater
}

export const useUserHasApplePaylaterCard = (): boolean | undefined => {
  const applePayState = useAppSelector(applePaylaterSelector)
  return applePayState.userHasApplePaylaterCard
}

export const useUserHasInstoreCard = (): boolean | undefined => {
  const applePayState = useAppSelector(applePaylaterSelector)
  return applePayState.userHasInstoreCard
}

export const useCardOwnerConsumerId = (): string | undefined => {
  const applePayState = useAppSelector(applePaylaterSelector)
  return applePayState.cardOwnerConsumerId
}

export const useApplePaylaterApplePay = (): OnfileApplePay | null | undefined => {
  const { started } = useCheckoutState()
  return started?.onfileApplePay
}

export const useApplePaylaterApplePayDisplay = (): { lastFour: string | null | undefined; expiryDate: string | undefined } => {
  const data = useApplePaylaterApplePay()
  const { lastFour, expiryMonth, expiryYear } = data ?? {}

  const formatExpiryDate = (month: number | null | undefined, year: number | null | undefined): string | undefined => {
    if (isNullOrUndefined(month) || isNullOrUndefined(year)) {
      return undefined
    }

    if (!Number.isInteger(month) || month < 1 || month > 12) {
      return undefined
    }

    if (!Number.isInteger(year) || year < 0) {
      return undefined
    }

    const paddedMonth = String(month).padStart(2, '0')
    const shortYear = String(year).padStart(2, '0').slice(-2)

    return `${paddedMonth}/${shortYear}`
  }

  const expiryDate = formatExpiryDate(expiryMonth, expiryYear)
  return { lastFour, expiryDate }
}
